import { useState, useEffect, useRef } from 'react';
import { Highlighter, MessageSquare, CreditCard, X, Check } from 'lucide-react';
import { HighlightColor } from './types';
import type { BookMetadata } from './types';
import ReferenceActionBar from './ReferenceActionBar';
import { useTranslation } from '../../i18n/useTranslation';

interface TextSelectionMenuWithFlashcardProps {
  selectedText: string;
  selection: Selection | null;
  menuPosition: { x: number; y: number } | null;
  onCreateHighlight: (color: HighlightColor, noteText?: string) => Promise<void>;
  onCreateFlashcard: (question: string, answer: string, sourceText: string) => Promise<void>;
  onClose: () => void;
  bookMetadata?: BookMetadata | null;
  locationLabel?: string;
}

type MenuMode = 'highlight' | 'flashcard';

export default function TextSelectionMenuWithFlashcard({
  selectedText,
  selection,
  menuPosition,
  onCreateHighlight,
  onCreateFlashcard,
  onClose,
  bookMetadata,
  locationLabel,
}: TextSelectionMenuWithFlashcardProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<MenuMode>('highlight');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Flashcard state
  const [flashcardQuestion, setFlashcardQuestion] = useState('');
  const [flashcardAnswer, setFlashcardAnswer] = useState('');
  const [isCreatingFlashcard, setIsCreatingFlashcard] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  if (previousFocusRef.current === null && typeof document !== 'undefined') {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
  }
  useEffect(() => {
    const dialog = menuRef.current;
    dialog?.focus();
    return () => {
      requestAnimationFrame(() => {
        if (!dialog?.isConnected && previousFocusRef.current) {
          previousFocusRef.current.focus?.();
        }
      });
    };
  }, []);

  const MENU_WIDTH = 320;
  const MENU_APPROX_HEIGHT = 200;
  const EDGE_GUTTER = 8;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const position = (() => {
    if (!menuPosition) return { top: 0, left: 0, showBelow: false, centered: false };
    if (isMobile) {
      return { top: 0, left: 0, showBelow: false, centered: true };
    }
    const rawY = Math.abs(menuPosition.y);
    const showBelow = menuPosition.y < 0 || rawY < MENU_APPROX_HEIGHT + EDGE_GUTTER;
    return {
      top: rawY,
      left: Math.max(
        EDGE_GUTTER,
        Math.min(menuPosition.x, window.innerWidth - MENU_WIDTH - EDGE_GUTTER)
      ),
      showBelow,
      centered: false,
    };
  })();

  // Close on click/touch outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = 'touches' in e ? e.touches[0]?.target : e.target;
      if (menuRef.current && target && !menuRef.current.contains(target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  // Reset state when selection changes
  useEffect(() => {
    setMode('highlight');
    setShowNoteInput(false);
    setNoteText('');
    setFlashcardQuestion('');
    setFlashcardAnswer('');
  }, [selectedText]);

  const handleHighlight = async (color: HighlightColor) => {
    try {
      if (showNoteInput && noteText.trim()) {
        await onCreateHighlight(color, noteText.trim());
      } else {
        await onCreateHighlight(color);
      }
      onClose();
    } catch (error) {
      console.error('Failed to create highlight:', error);
    }
  };

  const handleCreateFlashcard = async () => {
    if (!flashcardQuestion.trim() || !flashcardAnswer.trim()) {
      return;
    }

    setIsCreatingFlashcard(true);
    try {
      await onCreateFlashcard(
        flashcardQuestion.trim(),
        flashcardAnswer.trim(),
        selectedText
      );
      onClose();
    } catch (error) {
      console.error('Failed to create flashcard:', error);
    } finally {
      setIsCreatingFlashcard(false);
    }
  };

  const colors: { color: HighlightColor; label: string }[] = [
    { color: HighlightColor.Yellow, label: t('App.Reader.Highlight.Color.Yellow') },
    { color: HighlightColor.Green, label: t('App.Reader.Highlight.Color.Green') },
    { color: HighlightColor.Blue, label: t('App.Reader.Highlight.Color.Blue') },
    { color: HighlightColor.Pink, label: t('App.Reader.Highlight.Color.Pink') },
    { color: HighlightColor.Purple, label: t('App.Reader.Highlight.Color.Purple') },
    { color: HighlightColor.Orange, label: t('App.Reader.Highlight.Color.Orange') },
    { color: HighlightColor.Red, label: t('App.Reader.Highlight.Color.Red') },
    { color: HighlightColor.Teal, label: t('App.Reader.Highlight.Color.Teal') },
    { color: HighlightColor.Lime, label: t('App.Reader.Highlight.Color.Lime') },
    { color: HighlightColor.Amber, label: t('App.Reader.Highlight.Color.Amber') },
  ];

  if (!selectedText || !menuPosition) return null;

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-modal="false"
      aria-label={t('App.Reader.SelectionFlashcard.MenuAriaLabel')}
      tabIndex={-1}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      className={`text-selection-menu fixed z-[1100] ${
        position.centered
          ? 'inset-0 flex items-center justify-center'
          : position.showBelow ? '' : '-translate-y-full'
      }`}
      style={position.centered ? undefined : { top: `${position.top}px`, left: `${position.left}px` }}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-neutral-200 overflow-hidden animate-fade-in w-80 max-w-[calc(100vw-1rem)]">
        {/* Mode Tabs */}
        <div role="tablist" aria-label={t('App.Reader.SelectionFlashcard.TabListAriaLabel')} className="flex border-b border-neutral-200">
          <button
            role="tab"
            aria-selected={mode === 'highlight'}
            tabIndex={mode === 'highlight' ? 0 : -1}
            onClick={() => setMode('highlight')}
            className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
              mode === 'highlight'
                ? 'bg-white text-neutral-900 border-b-2 border-primary-500'
                : 'bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            <Highlighter className="w-3 h-3" aria-hidden="true" />
            {t('App.Reader.SelectionFlashcard.HighlightTab')}
          </button>
          <button
            role="tab"
            aria-selected={mode === 'flashcard'}
            tabIndex={mode === 'flashcard' ? 0 : -1}
            onClick={() => setMode('flashcard')}
            className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
              mode === 'flashcard'
                ? 'bg-white text-neutral-900 border-b-2 border-primary-500'
                : 'bg-neutral-50 text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            <CreditCard className="w-3 h-3" aria-hidden="true" />
            {t('App.Reader.SelectionFlashcard.FlashcardTab')}
          </button>
          <button
            onClick={onClose}
            aria-label={t('App.Reader.Selection.CloseLabel')}
            className="px-2 py-2 hover:bg-neutral-100 transition-colors border-l border-neutral-200"
          >
            <X className="w-3 h-3 text-neutral-500" aria-hidden="true" />
          </button>
        </div>

        {/* Highlight Mode */}
        {mode === 'highlight' && (
          <>
            {/* Color Palette */}
            <div className="p-3">
              <div className="grid grid-cols-5 gap-2">
                {colors.map(({ color, label }) => (
                  <button
                    key={color}
                    onClick={() => handleHighlight(color)}
                    aria-label={t('App.Reader.SelectionFlashcard.HighlightColorLabel', { color: label })}
                    className="w-10 h-10 rounded-md border-2 border-transparent hover:border-neutral-400 transition-all transform hover:scale-110 shadow-sm"
                    style={{ backgroundColor: color }}
                    title={label}
                  />
                ))}
              </div>
            </div>

            {/* Note Toggle */}
            <div className="px-3 pb-3">
              <button
                onClick={() => setShowNoteInput(!showNoteInput)}
                className="w-full px-3 py-2 text-xs font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-md transition-colors flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-3 h-3" />
                {showNoteInput ? t('App.Reader.SelectionFlashcard.HideNote') : t('App.Reader.SelectionFlashcard.AddNote')}
              </button>
            </div>

            {/* Note Input */}
            {showNoteInput && (
              <div className="px-3 pb-3">
                <label htmlFor="text-selection-flashcard-note" className="sr-only">{t('App.Reader.Selection.NotesLabel')}</label>
                <textarea
                  id="text-selection-flashcard-note"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t('App.Reader.SelectionFlashcard.NotePlaceholder')}
                  className="w-full px-3 py-2 text-xs border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  rows={3}
                />
                <div className="mt-2">
                  <button
                    onClick={() => {
                      if (noteText.trim()) {
                        handleHighlight(HighlightColor.Yellow);
                      }
                    }}
                    disabled={!noteText.trim()}
                    className="w-full px-3 py-2 text-xs font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('App.Reader.SelectionFlashcard.SaveWithNoteButton')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Flashcard Mode */}
        {mode === 'flashcard' && (
          <div className="p-3 space-y-3">
            <div>
              <label htmlFor="flashcard-question" className="block text-xs font-medium text-neutral-700 mb-1">
                {t('App.Reader.SelectionFlashcard.QuestionLabel')}
              </label>
              <input
                id="flashcard-question"
                type="text"
                value={flashcardQuestion}
                onChange={(e) => setFlashcardQuestion(e.target.value)}
                placeholder={t('App.Reader.SelectionFlashcard.QuestionPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label htmlFor="flashcard-answer" className="block text-xs font-medium text-neutral-700 mb-1">
                {t('App.Reader.SelectionFlashcard.AnswerLabel')}
              </label>
              <textarea
                id="flashcard-answer"
                value={flashcardAnswer}
                onChange={(e) => setFlashcardAnswer(e.target.value)}
                placeholder={t('App.Reader.SelectionFlashcard.AnswerPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                rows={3}
              />
            </div>

            <div>
              <label htmlFor="flashcard-source" className="block text-xs font-medium text-neutral-700 mb-1">
                {t('App.Reader.SelectionFlashcard.SourceLabel')}
              </label>
              <p id="flashcard-source" className="text-xs text-neutral-600 bg-neutral-50 px-3 py-2 rounded-md border border-neutral-200 line-clamp-3">
                {selectedText}
              </p>
            </div>

            <button
              onClick={handleCreateFlashcard}
              disabled={
                !flashcardQuestion.trim() ||
                !flashcardAnswer.trim() ||
                isCreatingFlashcard
              }
              className="w-full px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isCreatingFlashcard ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('App.Reader.SelectionFlashcard.Creating')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t('App.Reader.SelectionFlashcard.CreateButton')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Copy / Cite / Export actions (always visible under the tabs) */}
        <ReferenceActionBar
          selectedText={selectedText}
          bookMetadata={bookMetadata}
          locationLabel={locationLabel}
        />

        {/* Selected Text Preview (always visible) */}
        <div className="px-3 py-2 bg-neutral-50 border-t border-neutral-200">
          <p className="text-xs text-neutral-600 line-clamp-2">
            "{selectedText.slice(0, 150)}
            {selectedText.length > 150 ? '...' : ''}"
          </p>
        </div>
      </div>
    </div>
  );
}
